import { MdDownload } from "react-icons/md";
import TooltipIconButton from "components/Shared/TooltipIconButton";
import { FragmentType, getFragmentData, gql } from "__generated__/gql";
import { useAdminLazyQuery } from "utils/apollo/hooks/admin";
import { assertPresence } from "utils/assertions";
import { downloadFileUrl } from "utils/files/utils";
import useAsyncCompleteToast from "components/Shared/useAsyncCompleteToast";

const fragment = gql(/* GraphQL */ `
  fragment SelectedEmailDownloadOriginal on ReceivedEmailMessage {
    emailMessageId
    emailMessage {
      id
      sentAt
    }
  }
`);

const signedUrlQuery = gql(/* GraphQL */ `
  query SelectedEmailDownloadOriginalSignedUrl(
    $emailId: UUID!
    $filename: String
  ) {
    receivedEmailMessage(emailMessageId: $emailId) {
      emailMessageId
      s3ObjectKeyUrl(attachmentFilename: $filename)
    }
  }
`);

type Props = {
  data: FragmentType<typeof fragment> | null | undefined;
  filenamePrefix?: string;
};

export default function DownloadOriginal({ data, filenamePrefix = "" }: Props) {
  const rem = getFragmentData(fragment, data);
  const { showErrorToast } = useAsyncCompleteToast();

  const [getSignedUrl, { loading }] = useAdminLazyQuery(signedUrlQuery, {
    fetchPolicy: "network-only",
  });

  const handleClick = async () => {
    assertPresence(
      rem?.emailMessageId,
      "Expected email message ID to be present!",
    );

    const filename = `${filenamePrefix}email${
      rem.emailMessage?.sentAt
        ? `-${rem.emailMessage.sentAt.replace(/([:.]|\+00:00)/gi, "")}`
        : ""
    }.eml`;

    const { data: signedUrlData } = await getSignedUrl({
      variables: { emailId: rem.emailMessageId, filename },
    });

    const fileUrl = signedUrlData?.receivedEmailMessage?.s3ObjectKeyUrl;
    assertPresence(fileUrl, "Received unexpected response getting file URL");

    downloadFileUrl(fileUrl, filename);
  };

  return (
    <TooltipIconButton
      icon={<MdDownload />}
      label="Download original email"
      tooltipProps={{ placement: "bottom" }}
      variant="ghost"
      color="gray.600"
      size="sm"
      isDisabled={!rem?.emailMessageId}
      isLoading={loading}
      onClick={() => {
        handleClick().catch(showErrorToast);
      }}
    />
  );
}
